'use client'

import { useState } from 'react'
import Image from 'next/image'
import {Modal} from '@components/atoms/Modal'
import Text from '@components/atoms/Text'
import styles from './WishlistLimitModal.module.scss'
import { TypeWishlistCar } from '@context/WishlistContext'
import Button from '@components/atoms/Button'
import { useIsMobile } from '@hooks/use-mobile'
import { s3UrlLoader } from '@utils/internalImageLoder'
import { toKebabCase } from '@utils/stringFormatters'
import ImageResponsive from '@components/atoms/ImageResponsive'

interface WishlistLimitModalProps {
  isOpen: boolean
  onClose: () => void
  wishlistedCars: TypeWishlistCar[]
  onReplace: (oldCarId: string, newCar: TypeWishlistCar) => void
  newCar: TypeWishlistCar
}

const WishlistLimitModal = ({
  isOpen,
  onClose,
  wishlistedCars,
  onReplace,
  newCar
}: WishlistLimitModalProps) => {

  const isMobile = useIsMobile()
  const [selectedCarId, setSelectedCarId] = useState<string>('')
  const selectedCar = wishlistedCars.find(car => car.id === selectedCarId)

  const handleCarSelect = (carId: string) => {
    setSelectedCarId(carId)
  }

  const handleReplace = () => {
    if (selectedCarId) {
      onReplace(selectedCarId, newCar)
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overlayClassName={styles.modalOverlay}
      contentClassName={styles.modalContent}
      bodyClassName={styles.container}
      headerClassName={styles.headerClassName}
      preventScroll
    >
      <div className={styles.header}>
        <Text className={styles.warningText}>
          You can only wishlist up to 5 cars
        </Text>
        <Text className={styles.title}>
          Pick a car to replace in your wishlist
        </Text>
      </div>

      <div className={styles.carList}>
        {wishlistedCars.map((car) => (
          <button
            key={car.id}
            onClick={() => handleCarSelect(car.id)}
            className={styles.carButton}
          >
            <div
              className={`${styles.carImageContainer} ${
                selectedCarId === car.id ? styles.selected : ""
              }`}
            >
              <ImageResponsive
                filePath={`${toKebabCase(car.make)}/${toKebabCase(car.name)}`}
                imageTag={`Hero-Transparent`}
                alt={`${car.make} ${car.name}`}
                width={isMobile ? 40 : 60}
                height={isMobile ? 40 : 60}
                className={styles.carImage}
              />
            </div>
            <Text className={styles.carName}>{car.name}</Text>
          </button>
        ))}
      </div>

      {selectedCar && (
        <Text className={styles.replacementText}>
          You&apos;ll replace {selectedCar.name} with {newCar.name} in your
          wishlist
        </Text>
      )}

      <Button
        className={styles.replaceButton}
        onButtonClick={handleReplace}
        disabled={!selectedCarId}
      >
        Replace and wishlist
      </Button>
    </Modal>
  );
}

export default WishlistLimitModal 